import { Loader2, Shield } from 'lucide-react';
import type { ExcludedAlertState } from '@kirin/api-client/contracts/alerts';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { createMutationHandler } from '@/hooks/mutationHelpers';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { trackEvent } from '@/lib/analytics';
import { formatExcludedTooltipLines } from '@/lib/excludedAlertFormatting';
import {
  useRemoveAlertExclusion,
  type AlertExcludeScope,
} from '@/hooks/api/useAlertExclusions';
import { EXCLUDE_BUTTON_WIDTH_CLASS } from '@/components/AlertExcludeButton';
import { SUPPORT_READONLY_TOOLTIP } from '@/components/alertActionTooltipCopy';

/**
 * Props for {@link AlertRemoveExclusionButton}.
 */
export interface AlertRemoveExclusionButtonProps {
  /** Scope of the alert (org-shared or self/account). */
  scope: AlertExcludeScope;
  /** The alert key whose exclusion to remove. */
  alertKey: string;
  /** Optional className passed to the trigger Button. */
  className?: string;
  /** Optional callback invoked after a successful removal. */
  onRemoved?: () => void;
  /**
   * Optional exclude fragment from the grouped alert. When provided and the
   * alert is currently excluded, the trigger renders inside a tooltip
   * describing who excluded it, when, and until when. Passing
   * `null`/`undefined` (or an already-cleared state) keeps the bare button —
   * the tooltip is purely additive metadata, never required for the action
   * itself. Mirrors `AlertUnmuteButton`'s `mute` prop exactly.
   */
  exclude?: ExcludedAlertState | null;
  /**
   * Optional analytics dimensions threaded into the `alert_exclusion_removed`
   * Mixpanel event. See {@link AlertExcludeButton} for the rationale.
   */
  analyticsContext?: {
    severity?: string;
    type?: string;
  };
}

/**
 * One-click control to remove an existing exclusion from an alert
 * (KIR-2363 + KIR-2447).
 *
 * Identical gating to {@link AlertExcludeButton}: gated by the
 * `alert_ignore` feature flag (returns `null` while loading or disabled)
 * and, for org-scope, by `useIsOwnerOrManager` so members cannot remove
 * org-shared exclusions.
 *
 * Wraps `mutateAsync` with the shared {@link createMutationHandler} so
 * users see a consistent success/error toast across every consumer surface.
 *
 * Emits `alert_exclusion_removed` after a confirmed mutation success with
 * the same property shape as `alert_unmuted` (scope, alert_key, severity,
 * type). The analytics call is wrapped in its own try-block isolated from
 * `onRemoved` so a misbehaving consumer cannot suppress the event.
 */
export function AlertRemoveExclusionButton({
  scope,
  alertKey,
  className,
  onRemoved,
  exclude,
  analyticsContext,
}: AlertRemoveExclusionButtonProps): React.ReactElement | null {
  const { flags, isLoading: flagsLoading } = useFeatureFlags([
    flagsmithFeatureFlags.ALERT_IGNORE,
  ]);
  const ignoreEnabled = flags[flagsmithFeatureFlags.ALERT_IGNORE] === true;

  const writeGate = useOrgWriteGate(
    scope.kind === 'organization' ? scope.organizationId : undefined
  );

  const removeMutation = useRemoveAlertExclusion(scope);
  const { toast } = useToast();

  const handleRemove = createMutationHandler(removeMutation, toast, {
    successMessage: 'Exclusion removed',
    errorMessage: 'Failed to remove exclusion',
  });

  if (flagsLoading || !ignoreEnabled) {
    return null;
  }

  if (scope.kind === 'organization') {
    if (writeGate.isLoading) return null;
    if (writeGate.hidden) return null;
  }
  const writeDisabled = scope.kind === 'organization' && writeGate.disabled;

  const handleClick = async (): Promise<void> => {
    try {
      await handleRemove({ alertKey });
      // Both side effects below run only on a confirmed mutation success.
      // They are deliberately ISOLATED from each other: a throw in one must
      // never block the other (telemetry/UX divergence is much worse than a
      // single skipped event). Order: callback first (user-observable),
      // then analytics (background). Mirrors `AlertUnmuteButton` exactly.
      try {
        onRemoved?.();
      } catch {
        // Caller-supplied callbacks are out of our control. Swallowing here
        // ensures a misbehaving consumer can't suppress the analytics event
        // for a mutation the backend has already accepted.
      }
      trackEvent('alert_exclusion_removed', {
        scope: scope.kind,
        alert_key: alertKey,
        severity: analyticsContext?.severity,
        type: analyticsContext?.type,
      });
    } catch {
      // createMutationHandler already toasted; swallow so the click handler
      // does not bubble an unhandled rejection up to React's error boundary.
    }
  };

  const isPending = removeMutation.isPending;
  const tooltipLines = formatExcludedTooltipLines(exclude);

  const button = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn(EXCLUDE_BUTTON_WIDTH_CLASS, className)}
      disabled={isPending || writeDisabled}
      aria-label="Remove exclusion"
      onClick={() => void handleClick()}
    >
      {isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Shield className="size-4" />
      )}
      <span>Remove Exclusion</span>
    </Button>
  );

  if (writeDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{SUPPORT_READONLY_TOOLTIP}</TooltipContent>
      </Tooltip>
    );
  }

  if (!tooltipLines) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>
        <div className="flex flex-col gap-0.5 text-left">
          <span>Excluded by {tooltipLines.excluder}</span>
          <span>On {tooltipLines.excludedAt}</span>
          <span>Until {tooltipLines.excludedUntil}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

AlertRemoveExclusionButton.displayName = 'AlertRemoveExclusionButton';
